import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, MenuOpen } from '@mui/icons-material';
import ImageViewModal from 'components/shared/modals/ImageViewModal';
import AdminDesktopMenu from 'components/shared/menus/desktop/AdminDesktopMenu';
import ClientDesktopMenu from 'components/shared/menus/desktop/ClientDesktopMenu';
import OrgClientDesktopMenu from 'components/shared/menus/desktop/OrgClientDesktopMenu';
import SupervisorDesktopMenu from 'components/shared/menus/desktop/SupervisorDesktopMenu';
import TrainerDesktopMenu from 'components/shared/menus/desktop/TrainerDesktopMenu';
import UserAvatar from 'components/shared/user/UserAvatar';
import DrawerLogo from 'components/shared/drawers/DrawerLogo';
import DrawerLogoutButton from 'components/shared/drawers/DrawerLogoutButton';
import DrawerTourButton from 'components/shared/drawers/DrawerTourButton';
import DrawerUserInfo from 'components/shared/drawers/DrawerUserInfo';
import SidebarSearch from 'components/shared/sidebar/SidebarSearch';
import SidebarThemeToggle from 'components/shared/sidebar/SidebarThemeToggle';
import { roles } from 'common/constants/options';
import StyledDrawerMenuUserContainer from 'common/theme/drawer-menu/StyledDrawerMenuUserContainer';
import StyledSidebarFooter from 'common/theme/sidebar/StyledSidebarFooter';
import StyledSidebarHeader from 'common/theme/sidebar/StyledSidebarHeader';
import StyledSidebarNavZone from 'common/theme/sidebar/StyledSidebarNavZone';
import StyledSidebarPanel from 'common/theme/sidebar/StyledSidebarPanel';
import BeamContext from 'context/BeamContext';
import SidebarDrawerContext from 'context/SidebarDrawerContext';
import SidebarPanelContext, { matchesSearchQuery, normalizeSearchText } from 'context/SidebarPanelContext';
import authSlice from 'store/modules/auth';
import settingsSlice from 'store/modules/settings';

const getAvatarSize = (iconOnly, isMedium) => {
  if (iconOnly) {
    return 56;
  }

  return isMedium ? 50 : 72;
};

const getDesktopMenuByRole = (role, isOrgManager) => {
  // El gestor de empresa es un CLIENT con flag (no un rol propio, para que la misma persona
  // pueda entrenar sin abrir otra cuenta): su panel sustituye entero al menú de cliente.
  if (isOrgManager && role === roles.client) {
    return <OrgClientDesktopMenu />;
  }

  const roleType = {
    [roles.administrator]: () => <AdminDesktopMenu />,
    [roles.supervisor]: () => <SupervisorDesktopMenu />,
    [roles.client]: () => <ClientDesktopMenu />,
    [roles.trainer]: () => <TrainerDesktopMenu />,
    default: () => <AdminDesktopMenu />
  };

  return (roleType[role] || roleType.default)();
};

/**
 * Contenido ÚNICO del panel de navegación premium: lo renderizan tanto el drawer
 * permanente de desktop (variant="desktop", colapsable a rail) como el drawer
 * temporal de mobile (variant="mobile", siempre expandido, con botón cerrar).
 * Chrome: logo + colapsar/cerrar · buscador quick-jump · avatar/usuario · menú del
 * rol · footer (tour · tema · logout).
 */
function SidebarPanel({ variant = 'desktop' }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['buttons', 'labels']);
  const { beamsClient } = useContext(BeamContext);
  const { closeMobileSidebar, closeMobileSidebarThen } = useContext(SidebarDrawerContext);
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down('lg'));

  const miniDrawer = useSelector((state) => state.settings.config.miniDrawer);
  const user = useSelector((state) => state.auth.user);
  const [photoViewOpen, setPhotoViewOpen] = useState(false);
  const [query, setQuery] = useState('');
  const itemsRef = useRef(new Map());

  const isMobile = variant === 'mobile';
  const iconOnly = !isMobile && Boolean(miniDrawer);

  const avatarSize = useMemo(() => getAvatarSize(iconOnly, isMedium), [iconOnly, isMedium]);
  const avatarTextSize = useMemo(() => (isMedium ? 25 : 30), [isMedium]);

  const registerItem = useCallback((key, item) => {
    itemsRef.current.set(key, item);
  }, []);

  const unregisterItem = useCallback((key) => {
    itemsRef.current.delete(key);
  }, []);

  // `afterClose` se ejecuta cuando el panel HA TERMINADO de cerrarse. En escritorio, donde no hay
  // nada que cerrar, corre de inmediato.
  const onNavigate = useCallback(
    (afterClose) => {
      setQuery('');
      if (isMobile) {
        closeMobileSidebarThen(afterClose);
        return;
      }
      if (afterClose) afterClose();
    },
    [isMobile, closeMobileSidebarThen]
  );

  const contextValue = useMemo(
    () => ({ iconOnly, isMobile, query, onNavigate, registerItem, unregisterItem }),
    [iconOnly, isMobile, query, onNavigate, registerItem, unregisterItem]
  );

  const logout = () => {
    if (beamsClient) beamsClient.stop().catch(console.error);
    dispatch(authSlice.actions.logout());
  };

  const toggleMiniDrawer = () => {
    if (!user?.settings?.id) return;
    dispatch(
      settingsSlice.actions.update({
        id: user.settings.id,
        input: { mini_drawer: !miniDrawer }
      })
    );
  };

  const expandDrawer = useCallback(() => {
    if (!user?.settings?.id) return;
    dispatch(
      settingsSlice.actions.update({
        id: user.settings.id,
        input: { mini_drawer: false }
      })
    );
  }, [dispatch, user?.settings?.id]);

  // Enter en el buscador: navega al primer ítem registrado que matchee el filtro.
  const handleSearchSubmit = useCallback(() => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return;
    const match = Array.from(itemsRef.current.values()).find((item) =>
      normalizeSearchText(item.label).includes(normalizedQuery)
    );
    if (match) {
      onNavigate(() => navigate(match.path));
    }
  }, [navigate, onNavigate, query]);

  const collapseLabel = miniDrawer
    ? t('expand', { ns: 'buttons' })
    : t('collapse', { ns: 'buttons' });

  // Estado vacío del filtro: el registro de ítems se llena al renderizar el menú, así
  // que en el render disparado por `query` ya está completo.
  const trimmedQuery = query.trim();
  const hasMatches =
    !trimmedQuery ||
    Array.from(itemsRef.current.values()).some((item) => matchesSearchQuery(item.label, trimmedQuery));

  return (
    <SidebarPanelContext.Provider value={contextValue}>
      <StyledSidebarPanel>
        <StyledSidebarHeader iconOnly={iconOnly}>
          <DrawerLogo />
          {isMobile ? (
            <IconButton
              aria-label={t('closeMenu', { ns: 'buttons', defaultValue: 'Cerrar menú' })}
              color="menuItem"
              onClick={closeMobileSidebar}>
              <Close />
            </IconButton>
          ) : (
            /* También en admin: los grupos colapsables ya saben comportarse en rail (expanden
               el panel al pulsarlos), y sin este botón un admin con el drawer contraído se
               quedaba sin forma de volver a expandirlo. */
            <Tooltip title={collapseLabel} placement="right">
              <IconButton aria-label={collapseLabel} color="menuItem" onClick={toggleMiniDrawer}>
                {/* MenuOpen apunta a la izquierda (colapsar); espejado = expandir. */}
                <MenuOpen sx={{ transform: miniDrawer ? 'scaleX(-1)' : 'none' }} />
              </IconButton>
            </Tooltip>
          )}
        </StyledSidebarHeader>
        <SidebarSearch
          enableHotkey={!isMobile}
          iconOnly={iconOnly}
          query={query}
          onExpandRequest={expandDrawer}
          onQueryChange={setQuery}
          onSubmit={handleSearchSubmit}
        />
        <StyledDrawerMenuUserContainer miniDrawer={iconOnly}>
          {/* Con foto es interactivo (abre el visor): botón real para teclado/lector. */}
          <Box
            component={user?.photo ? 'button' : 'div'}
            type={user?.photo ? 'button' : undefined}
            aria-label={user?.photo ? t('viewProfilePhoto', { ns: 'buttons', defaultValue: 'Ver foto de perfil' }) : undefined}
            onClick={user?.photo ? () => setPhotoViewOpen(true) : undefined}
            sx={{
              display: 'inline-flex',
              borderRadius: '50%',
              border: 0,
              padding: 0,
              background: 'none',
              cursor: user?.photo ? 'pointer' : 'default'
            }}>
            <UserAvatar border size={avatarSize} textSize={avatarTextSize} user={user} />
          </Box>
          {!iconOnly && <DrawerUserInfo />}
        </StyledDrawerMenuUserContainer>
        <StyledSidebarNavZone>
          {getDesktopMenuByRole(user.role, user.is_org_manager)}
          {!hasMatches && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', px: 1.5, py: 2, textAlign: 'center' }}>
              {t('sidebarSearchNoResults', { ns: 'labels', defaultValue: 'Sin resultados en el menú' })}
            </Typography>
          )}
        </StyledSidebarNavZone>
        <StyledSidebarFooter>
          {/* Cliente, entrenador y gestor de empresa tienen tour propio (grupos distintos). */}
          {(user.role === roles.client || user.role === roles.trainer) && (
            <DrawerTourButton />
          )}
          <SidebarThemeToggle iconOnly={iconOnly} />
          <DrawerLogoutButton onClick={logout} />
        </StyledSidebarFooter>
        <ImageViewModal
          open={photoViewOpen}
          src={user?.photo}
          alt={user?.full_name}
          onClose={() => setPhotoViewOpen(false)}
        />
      </StyledSidebarPanel>
    </SidebarPanelContext.Provider>
  );
}

SidebarPanel.propTypes = {
  variant: PropTypes.oneOf(['desktop', 'mobile'])
};

export default SidebarPanel;
